import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Order} from '../_models/order';
import {PrintJob} from '../_models/print-job';
import {Organization} from '../_models/organization';
import {PickupLocation} from '../_models/pickup-location';
import {CustomerAreaPosition} from '../_models/customer-area-position';
import {CashSessionSummary} from '../_models/cash-information/cash-session-summary';
import {CashSession} from '../_models/cash-information/cash-session';
import {Timecard} from '../_models/timecard';
import {EpsonPrinterService} from './epson-printer.service';
import {StarPrinterService} from './star-printer.service';
import {ModalController, Platform} from '@ionic/angular';
import {AndroidPermissions} from '@awesome-cordova-plugins/android-permissions/ngx';
import {Tab} from '../_models/tab';
import {GiftCard} from '../_models/gift-card';
import {WifiPrinterService} from './wifi-printer.service';
import {OrganizationService} from './organization.service';
import {PickupLocationService} from './pickup-location.service';
import {LoadingService} from './loading.service';
import {WifiPrinter} from '../_models/wifi-printer';
import {Membership} from '../_models/membership';
import {OrderPrintOptions} from '../_constants/order-print-options';
import {ChangeCustomerPositionComponent} from '../_components/change-customer-position/change-customer-position.component';
import {ChangePartySizeComponent} from '../_components/change-party-size/change-party-size.component';
import {PersonService} from './person.service';
import {TabService} from './tab.service';
import {v4 as uuidv4} from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class PrinterService {
    type: string;
    currentOrganization: Organization;
    currentPickupLocation: PickupLocation;
    wifiPrinters: WifiPrinter[];

    public connected: Observable<string>;
    private connectedSubject: BehaviorSubject<string>;

    public failedPrintJobs: Observable<PrintJob[]>;
    private failedPrintJobsSubject: BehaviorSubject<PrintJob[]>;

    constructor(
        private epsonPrinterService: EpsonPrinterService,
        private starPrinterService: StarPrinterService,
        private organizationService: OrganizationService,
        private pickupLocationService: PickupLocationService,
        private tabService: TabService,
        private personService: PersonService,
        private wifiPrinterService: WifiPrinterService,
        private loadingService: LoadingService,
        private modalController: ModalController,
        private platform: Platform,
        private androidPermissions: AndroidPermissions
    ) {
        this.connectedSubject = new BehaviorSubject<string>(null);
        this.connected = this.connectedSubject.asObservable();

        this.failedPrintJobsSubject = new BehaviorSubject<PrintJob[]>([]);
        this.failedPrintJobs = this.failedPrintJobsSubject.asObservable();

        this.organizationService.current.subscribe(o => {
            if (!!o && (!this.currentOrganization || this.currentOrganization.id !== o.id)) {
                this.currentOrganization = !!o ? new Organization(o) : null;
                this.refresh();
            } else {
                this.currentOrganization = !!o ? new Organization(o) : null;
            }
        });

        this.pickupLocationService.current.subscribe(p => {
            this.currentPickupLocation = !!p ? new PickupLocation(p) : null;
        });

        this.epsonPrinterService.connected.subscribe(c => {
            if (c) {
                this.type = 'EPSON';
                this.connectedSubject.next(c);
            } else if (this.type === 'EPSON' && !c) {
                this.type = null;
                this.connectedSubject.next(c);
            }
        });

        this.starPrinterService.connected.subscribe(c => {
            if (c) {
                this.type = 'STAR';
                this.connectedSubject.next(c);
            } else if (this.type === 'STAR' && !c) {
                this.type = null;
                this.connectedSubject.next(c);
            }
        });

        this.wifiPrinterService.wifiPrinters.subscribe(p => {
            this.wifiPrinters = !!p ? p : [];
            if (this.wifiPrinters.length > 0) {
                this.wifiPrinters.forEach(async (w) => {
                    await this.initializeWifiPrinter(w);
                });
            }
        });
    }

    async refresh() {
        if (!!this.currentOrganization) {
            const printers = await this.wifiPrinterService.findByOrganizationId(this.currentOrganization.id).toPromise() as WifiPrinter[];

            this.wifiPrinterService.setPrinters(printers);
            if (!this.wifiPrinterService.currentValue) {
                const current = localStorage.getItem(`WIFI_PRINTER_${this.currentOrganization.id}`);
                if (!!current) {
                    const w = printers.find(p => p.id === parseInt(current, 10));
                    if (!!w) {
                        const connected = await this.initializeWifiPrinter(w, true);
                        if (connected) {
                            this.wifiPrinterService.setCurrent(new WifiPrinter(w));
                        }
                    }
                }
            }
        }
    }

    async initializeWifiPrinter(wifiPrinter: WifiPrinter, saveConnection = false) {
        if (!!wifiPrinter) {
            if (wifiPrinter.type === 'EPSON') {
                return this.epsonPrinterService.initializeWifiPrinter(wifiPrinter, saveConnection);
            } else if (wifiPrinter.type === 'STAR') {
                return this.starPrinterService.initializeWifiPrinter(wifiPrinter);
            }
        }
    }

    async connectViaBlueTooth() {
        try {
            if (this.platform.is('android')) {
                await this.requestPermission(this.androidPermissions.PERMISSION.BLUETOOTH_CONNECT);
                await this.requestPermission(this.androidPermissions.PERMISSION.BLUETOOTH_SCAN);
            }

            if (this.platform.is('android') || this.platform.is('ios')) {
                console.log('DISCOVERING STAR');
                await this.starPrinterService.discover();
                if (!!this.starPrinterService.port) {
                    this.type = 'STAR';
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    async requestPermission(permission: string) {
        console.log('REQUESTING PERMISSION', permission);
        const result = await this.androidPermissions.checkPermission(permission);
        if (!!result && !result.hasPermission) {
            console.log('REQUESTING PERMISSION', permission);
            const res = await this.androidPermissions.requestPermission(permission);
            console.log(res);
        }
    }

    openDrawer() {
        if (this.type === 'EPSON') {
            this.epsonPrinterService.openDrawer();
        } else if (this.type === 'STAR') {
            this.starPrinterService.openDrawer();
        }
    }

    async printClaimTab(tab: Tab) {
        if (this.type === 'EPSON') {
            await this.epsonPrinterService.printClaimTab(tab);
        } else if (this.type === 'STAR') {
            await this.starPrinterService.printClaimTab(tab);
        }
    }

    async printClaimMembership(member: Membership) {
        if (this.type === 'EPSON') {
            await this.epsonPrinterService.printClaimMembership(member);
        } else if (this.type === 'STAR') {
            await this.starPrinterService.printClaimMembership(member);
        }
    }

    printGiftCardBalances(organization: Organization, giftCards: GiftCard[]) {
        if (this.type === 'EPSON') {
            this.epsonPrinterService.printGiftCardBalances(organization, giftCards);
        } else if (this.type === 'STAR') {
            this.starPrinterService.printGiftCardBalances(organization, giftCards);
        }
    }

    async print(msg): Promise<boolean> {
        if (this.type === 'EPSON') {
            return this.epsonPrinterService.print(msg);
        } else if (this.type === 'STAR') {
            return this.starPrinterService.print(msg);
        }
    }

    printCashSession(cashSession: CashSession, summary: CashSessionSummary, organization: Organization) {
        if (this.type === 'EPSON') {
            this.epsonPrinterService.printCashSession(cashSession, summary, organization);
        } else if (this.type === 'STAR') {
            this.starPrinterService.printCashSession(cashSession, summary, organization);
        }
    }

    printTimeCard(timeCard: Timecard, organization: Organization) {
        if (this.type === 'EPSON') {
            this.epsonPrinterService.printTimeCard(timeCard, organization);
        } else if (this.type === 'STAR') {
            this.starPrinterService.printTimeCard(timeCard, organization);
        }
    }

    async printAllOrderInformation(order: Order, tab: Tab, silent = true) {
        const wifiPrinter = this.wifiPrinterService.currentValue;
        if (!!order) {
            if (!!tab) {
                order.parentId = tab.id;
            }

            if (!order.customerAreaPosition && !!tab && !!tab.customerAreaPosition) {
                order.customerAreaPosition = new CustomerAreaPosition(tab.customerAreaPosition);
            }
            order.partySize = !!tab ? tab.partySize : null;

            if (!!wifiPrinter && !!this.currentPickupLocation &&
                (this.currentPickupLocation.orderPrintOption === OrderPrintOptions.ALL
                    || this.currentPickupLocation.orderPrintOption === OrderPrintOptions.REQUIRE_NAME)) {
                if ((this.currentPickupLocation.orderPrintOption === OrderPrintOptions.REQUIRE_NAME
                        || this.currentOrganization.requestNameForOrderPrint)
                    && !!tab && !tab.person && !tab.nameOverride) {
                    const validationRequirement = this.personService.requirePersonInformation(tab, order);
                    validationRequirement.nameOnly = true;
                    validationRequirement.title = 'Tab Name';
                    const person = await this.personService.editPersonInformation(validationRequirement);

                    if (!!person) {
                        const t = await this.tabService.updatePerson(tab.id, person).toPromise() as Tab;
                        if (!!t.person.firstName && !!t.person.lastName) {
                            order.tabPersonName = t.person.firstName + ' ' + t.person.lastName;
                        } else if (!!t.person.firstName) {
                            order.tabPersonName = t.person.firstName;
                        } else if (!!t.person.lastName) {
                            order.tabPersonName = t.person.lastName;
                        }

                        await this.printAllOrderInformation(order, t);
                    } else {
                        await this.printOrder(order, wifiPrinter, true);
                    }
                } else {
                    if (!order.tabPersonName && !!tab) {
                        if (!!tab.nameOverride) {
                            order.tabPersonName = tab.nameOverride;
                        } else if (!!tab.person) {
                            if (!!tab.person.firstName && !!tab.person.lastName) {
                                order.tabPersonName = tab.person.firstName + ' ' + tab.person.lastName;
                            } else if (!!tab.person.firstName) {
                                order.tabPersonName = tab.person.firstName;
                            } else if (!!tab.person.lastName) {
                                order.tabPersonName = tab.person.lastName;
                            }
                        }
                    }
                    await this.printOrder(order, wifiPrinter, true);
                }
            }
            if (!!order.items && order.items.some(i => !!i.menuItem && i.menuItem.wifiPrinter)) {
                const person = tab.person;
                if (!person && !tab.nameOverride && this.currentOrganization.requestNameForOrderPrint) {
                    await this.getRequiredPerson(tab, order);
                }

                if (tab.nameOverride) {
                    order.tabPersonName = tab.nameOverride;
                } else if (!!tab.person) {
                    if (!!tab.person.firstName && !!tab.person.lastName) {
                        order.tabPersonName = tab.person.firstName + ' ' + tab.person.lastName;
                    } else if (!!tab.person.firstName) {
                        order.tabPersonName = tab.person.firstName;
                    } else if (!!tab.person.lastName) {
                        order.tabPersonName = tab.person.lastName;
                    }
                }

                if (this.currentOrganization.requestCustomerAreaForOrderPrint && !tab.customerAreaPosition) {
                    await this.getRequiredCustomerAreaPosition(tab, order);
                }

                if (this.currentOrganization.requestPartySizeForOrderPrint && !tab.partySize) {
                    await this.getRequiredPartySize(tab, order);
                }

                await this.delegateItemPrint(order, silent);
            }
        }
    }

    async getRequiredPerson(tab: Tab, order: Order) {
        const validationRequirement = this.personService.requirePersonInformation(tab, order);
        validationRequirement.nameOnly = true;
        validationRequirement.title = 'Tab Name';
        const person = await this.personService.editPersonInformation(validationRequirement);

        if (!!person) {
            this.loadingService.present();
            const t = await this.tabService.updatePerson(tab.id, person).toPromise() as Tab;
            this.loadingService.dismiss();
            this.tabService.setEvent(t);
            tab.person = t.person;
            order.person = t.person;
        } else {
            await this.getRequiredPerson(tab, order);
        }
    }

    async getRequiredCustomerAreaPosition(tab: Tab, order: Order) {
        if (!!this.currentOrganization.customerAreas && this.currentOrganization.customerAreas.length > 0) {
            const modal = await this.modalController.create({
                component: ChangeCustomerPositionComponent,
                componentProps: {
                    organization: this.currentOrganization,
                    pickupLocation: this.currentPickupLocation
                },
                cssClass: 'menu-modal'
            });

            await modal.present();

            const dataReturned = await modal.onDidDismiss();
            if (!!dataReturned && !!dataReturned.data) {
                this.loadingService.present();
                const t = await this.tabService.updateCustomerPosition(tab.id, dataReturned.data.id).toPromise() as Tab;
                this.loadingService.dismiss();
                this.tabService.setEvent(t);
                tab.customerAreaPosition = new CustomerAreaPosition(t.customerAreaPosition);
                order.customerAreaPosition = new CustomerAreaPosition(t.customerAreaPosition);
            } else {
                await this.getRequiredCustomerAreaPosition(tab, order);
            }
        }
    }

    async getRequiredPartySize(tab: Tab, order: Order) {
        const modal = await this.modalController.create({
            component: ChangePartySizeComponent,
            cssClass: 'menu-modal'
        });

        await modal.present();

        const dataReturned = await modal.onDidDismiss();
        if (!!dataReturned && !!dataReturned.data) {
            this.loadingService.present();
            const t = await this.tabService.updatePartySize(tab.id, dataReturned.data !== 'NONE' ? dataReturned.data : null)
                .toPromise() as Tab;
            this.loadingService.dismiss();
            this.tabService.setEvent(t);
            tab.partySize = t.partySize;
            order.partySize = t.partySize;
        } else {
            await this.getRequiredPartySize(tab, order);
        }
    }

    async delegateItemPrint(order: Order, silent = true) {
        if (!!order) {
            const printJobs = order.items.reduce((acc, item) => {
                if (!!item.menuItem && !!item.menuItem.wifiPrinter) {
                    const existing = acc.find(a => a.wifiPrinter.id === item.menuItem.wifiPrinter.id);
                    if (!!existing) {
                        existing.items.push(item);
                    } else {
                        acc.push({
                            wifiPrinter: item.menuItem.wifiPrinter,
                            items: [item]
                        });
                    }
                }
                return acc;
            }, []);

            for (let p of printJobs) {
                const copy = new Order(order);
                copy.items = p.items;
                try {
                    await this.printOrder(copy, p.wifiPrinter, true);
                } catch (error) {
                    console.log(error);
                }
            }
        }
    }

    async printOrder(order: Order, wifiPrinter: WifiPrinter, silent = false, key = null) {
        const orderObject = new Order(order);
        const items = this.sortOrderItems(orderObject);
        const usePrinter = !!this.wifiPrinters && !!wifiPrinter ? this.wifiPrinters.find(p => p.id === wifiPrinter.id) : wifiPrinter;
        const type = !!wifiPrinter ? wifiPrinter.type : this.type;
        let printed = false;
        if (type === 'EPSON') {
            const currentPrinter = this.wifiPrinterService.currentValue;
            printed = await this.epsonPrinterService.printOrder(orderObject, items, usePrinter, silent) as boolean;
            if (!!usePrinter && !!currentPrinter && currentPrinter.id !== usePrinter.id) {
                await this.initializeWifiPrinter(currentPrinter);
            }
        } else if (type === 'STAR') {
            printed = await this.starPrinterService.printOrder(order, items, usePrinter, silent) as boolean;
        }

        if (!printed && silent) {
            this.addFailedJob(new PrintJob({
                key: !!key ? key : uuidv4(),
                wifiPrinter,
                order,
                printType: 'ORDER_PRINT'
            }));
        } else if (!!key) {
            this.clearJob(new PrintJob({
                key,
                wifiPrinter,
                order,
                printType: 'ORDER_PRINT'
            }));
        }
    }

    sortOrderItems(order: Order) {
        let items = [...order.items];
        items = items.sort((a, b) => {
            let aVal: any = a.orderType;
            let bVal: any = b.orderType;
            if (aVal < bVal) {
                return -1;
            }
            if (aVal > bVal) {
                return 1;
            }
            return a.addIndex - b.addIndex;
        });
        return items;
    }

    isConnected() {
        return !!this.type;
    }

    get columns() {
        if (this.type === 'EPSON' && !!this.epsonPrinterService.currentWifiPrinter) {
            return this.epsonPrinterService.currentWifiPrinter.columns;
        } else if (this.type === 'STAR') {
            return this.starPrinterService.columns;
        } else {
            return 38;
        }
    }

    clearJob(printJob: PrintJob) {
        const current = this.failedPrintJobsSubject.getValue();
        let index = current.findIndex(p => p.order.key === printJob.order.key);
        if (index > -1) {
            const found = current[index];
            if (!!found.timeout) {
                clearTimeout(found.timeout);
            }
            current.splice(index, 1);
            this.failedPrintJobsSubject.next(current);
        }
    }

    addFailedJob(printJob: PrintJob) {
        const current = this.failedPrintJobsSubject.getValue();
        let found = current.find(p => p.key === printJob.key);

        const job = !!found ? found : printJob;
        if (!job.retry) {
            job.retry = 0;
        }

        job.retry++;

        if (job.retry <= 4) {
            job.timeout = setTimeout(() => {
                this.retryPrintJob(job);
            }, 15000);
        }

        job.retrying = false;

        if (!found) {
            current.push(job);
            this.failedPrintJobsSubject.next(current);
        }
    }

    async retryPrintJob(printJob: PrintJob, silent = true) {
        if (!!printJob.timeout) {
            clearTimeout(printJob.timeout);
        }
        if (printJob.printType === 'ORDER_PRINT') {
            printJob.retrying = true;
            try {
                await this.printOrder(printJob.order, printJob.wifiPrinter, silent, printJob.key);
            } finally {
                printJob.retrying = false;
            }
        }
    }
}
